import { type Palette, palette } from './customPalette';
import type { CustomColors, GeneratedColors, StandardColor, StandardColorStrengths } from './types';

const convertStandardColor =
    (theme: 'light' | 'dark') =>
    (color: Record<StandardColorStrengths, { light: string; dark: string }>): StandardColor =>
        Object.entries(color).reduce(
            (acc, [key, value]) => ({
                ...acc,
                [key]: value[theme],
            }),
            {} as StandardColor,
        );

const generateColors = (colorPalette: Palette, mode: 'light' | 'dark'): GeneratedColors => {
    const { primary, red, green, yellow, interactive } = colorPalette;

    const convertStandardColorWithTheme = convertStandardColor(mode);

    return {
        primary: convertStandardColorWithTheme(primary),
        red: convertStandardColorWithTheme(red),
        green: convertStandardColorWithTheme(green),
        yellow: convertStandardColorWithTheme(yellow),

        neutral1: colorPalette.neutral1[mode],
        neutral2: colorPalette.neutral2[mode],
        background: {
            primary: colorPalette.background.primary[mode],
            secondary: colorPalette.background.secondary[mode],
            input: colorPalette.background.input[mode],
        },
        surface: {
            primary: colorPalette.surface.primary[mode],
            secondary: colorPalette.surface.secondary[mode],
            primary50: colorPalette.surface.primary50[mode],
            primary100: colorPalette.surface.primary100[mode],
            red50: colorPalette.surface.red50[mode],
            red100: colorPalette.surface.red100[mode],
            green50: colorPalette.surface.green50[mode],
            green100: colorPalette.surface.green100[mode],
            yellow50: colorPalette.surface.yellow50[mode],
            yellow100: colorPalette.surface.yellow100[mode],
        },
        content: {
            primary: colorPalette.content.primary[mode],
            secondary: colorPalette.content.secondary[mode],
            disabled: colorPalette.content.disabled[mode],
            white: colorPalette.content.white[mode],
            black: colorPalette.content.black[mode],
            buttonText1: colorPalette.content.buttonText1[mode],
            buttonText2: colorPalette.content.buttonText2[mode],
        },
        border: {
            primary: colorPalette.border.primary[mode],
            secondary: colorPalette.border.secondary[mode],
        },
        interactive: {
            content: {
                idle: interactive.content.idle[mode],
                hover: interactive.content.hover[mode],
                pressed: interactive.content.pressed[mode],
                pressedHover: interactive.content.pressedHover[mode],
                disabled: interactive.content.disabled[mode],
            },
            primary: {
                idle: interactive.primary.idle[mode],
                hover: interactive.primary.hover[mode],
                pressed: interactive.primary.pressed[mode],
                pressedHover: interactive.primary.pressedHover[mode],
                disabled: interactive.primary.disabled[mode],
            },
            red: {
                idle: interactive.red.idle[mode],
                hover: interactive.red.hover[mode],
                pressed: interactive.red.pressed[mode],
                pressedHover: interactive.red.pressedHover[mode],
                disabled: interactive.red.disabled[mode],
            },
            green: {
                idle: interactive.green.idle[mode],
                hover: interactive.green.hover[mode],
                pressed: interactive.green.pressed[mode],
                pressedHover: interactive.green.pressedHover[mode],
                disabled: interactive.green.disabled[mode],
            },
            yellow: {
                idle: interactive.yellow.idle[mode],
                hover: interactive.yellow.hover[mode],
                pressed: interactive.yellow.pressed[mode],
                pressedHover: interactive.yellow.pressedHover[mode],
                disabled: interactive.yellow.disabled[mode],
            },
        },
    };
};

const customColors = {
    sidebarNav: {
        inactive: {
            foreground: { light: '#8a8a8a', dark: '#8a8a8a' },
        },
        active: {
            foreground: { light: '#5489dc', dark: '#5489dc' },
            background: { light: '#ffffff73', dark: '#343434' },
        },
    },
};

const generateThemeColors = (colorPalette: Palette, theme: 'light' | 'dark'): GeneratedColors & CustomColors => ({
    ...generateColors(palette, theme),
    // This probably shouldn't be here but currently there's no way to extend theme from app.
    tasks: {
        tag: {
            translation: '#76BEDB',
            review: '#74B766',
            due: '#DEA829',
            lqa_by_ai: '#F6F2FF',
            automatic_translation: '#F6F2FF',
        },
    },
    sidebarNav: {
        inactive: {
            foreground: customColors.sidebarNav.inactive.foreground[theme],
        },
        active: {
            foreground: customColors.sidebarNav.active.foreground[theme],
            background: customColors.sidebarNav.active.background[theme],
        },
    },
    editor: {
        placeholderMismatch: '#7130BE',
    },
    translationTokens:
        theme === 'dark'
            ? {
                  punctuation: '#a678dd',
                  name: '#f5bdff',
                  data: '#d1a8ff',
                  value: '#e8a8ff',
                  whitespace: 'rgba(255, 0, 0, 1)',
                  empty: '#dd1144',
                  referenceError: 'red',
                  referencedText: '#009900',
                  referencedTextHover: '#007e00',
              }
            : {
                  punctuation: '#7130be',
                  name: '#e659ff',
                  data: '#9941ff',
                  value: '#ce42ff',
                  whitespace: 'rgba(255, 0, 0, 1)',
                  empty: '#dd1144',
                  referenceError: 'red',
                  referencedText: '#009900',
                  referencedTextHover: '#007e00',
              },
    lokaliseAi: {
        logo: theme === 'dark' ? '#FFFFFF' : '#7A4BED',
        tag: {
            background: theme === 'dark' ? '#363638' : '#F6F2FF',
        },
        progressBar: {
            medium: theme === 'dark' ? '#4a0dc5' : '#daccf5',
        },
    },
});

export default generateThemeColors;
